import { CardNumberElement } from '@stripe/react-stripe-js';
import type * as stripeJs from '@stripe/stripe-js';
import type { StripeCardNumberElement } from '@stripe/stripe-js';
import type {
  PaymentRequest,
  CanMakePaymentResult,
  PaymentRequestWallet,
  PaymentRequestItem,
} from '@stripe/stripe-js/types/stripe-js/payment-request';
import type { PaymentMethodResult } from '@stripe/stripe-js/types/stripe-js/stripe';
import logEvent from 'analytics/logEvent';
import { QueryParser } from 'domains/common/queryParser';

import type { AppStore, AppState } from 'redux-store';
import type { AttachPaymentsMethodResponseType } from 'redux-store/slices/paymentsMethods/types';
import { chargeFinish } from 'redux-store/slices/purchases';
import type { ChargePurchasesResponseType } from 'redux-store/slices/purchases/types';
import attachPaymentsMethodThunk from 'redux-store/thunks/paymentsMethods/attachPaymentsMethodThunk';
import chargePurchaseThunk from 'redux-store/thunks/purchases/chargePurchaseThunk';

export class StripeProcessing {
  async processing({
    coupon,
    plan,
    stripe,
    elements,
    store,
    walletCardId,
  }: {
    coupon?: string;
    plan: { id: number; interval: string; discount: number; price: number };
    stripe: stripeJs.Stripe;
    elements: stripeJs.StripeElements;
    store: AppStore;
    walletCardId?: string | null;
  }): Promise<ChargePurchasesResponseType | false> {
    const cardElement = elements.getElement(CardNumberElement);

    if (!cardElement) {
      return false;
    }

    let paymentId = walletCardId || null;

    if (!paymentId) {
      try {
        const result = await this.createPaymentMethod(cardElement, stripe);

        logEvent('Web_Payment_New_Card_Created');

        if (result === null) {
          return false;
        }

        const { error, paymentMethod } = result;

        if (error) {
          logEvent('Web_Payment_Processing_Failed', { error: error.code });

          return false;
        }

        const attachResult = await this.attachPayment({
          paymentMethodId: paymentMethod.id,
          store,
        });

        if (attachResult.success) {
          paymentId = attachResult.result.id;
          logEvent('Web_Payment_New_Card_Attached');
        } else {
          logEvent('Web_Payment_Processing_Failed', {
            error: attachResult.result.code,
          });
        }
      } catch (e) {
        console.error('ERROR: ', e);
      }
    }

    if (!paymentId) {
      return false;
    }

    try {
      return this.chargePayment({
        coupon,
        plan,
        paymentId,
        store,
      });
    } catch (e) {
      console.error('ERROR: ', e);
    }

    return false;
  }

  async processingButton({
    coupon,
    plan,
    stripe,
    info,
    store,
    onPay,
    onPayBefore,
  }: {
    coupon?: string;
    plan: { id: number; interval: string; discount: number; price: number };
    info: {
      currency: string;
      title: string;
      totalPrice: number;
      items?: PaymentRequestItem[];
    };
    stripe: stripeJs.Stripe;
    store: AppStore;
    onPay?: (args: {
      paymentName: PaymentRequestWallet | string;
      isSuccess: boolean;
    }) => void | Promise<void>;
    onPayBefore?: () => void;
  }): Promise<[PaymentRequest, string | null] | null> {
    const paymentRequest = stripe.paymentRequest({
      country: 'US',
      currency: info.currency,
      total: {
        label: info.title,
        amount: +(+info.totalPrice * 100).toFixed(2),
      },
      displayItems: info.items,
      requestPayerName: true,
      requestPayerEmail: true,
    });

    paymentRequest.on('cancel', () => {
      logEvent('Web_Payment_Confirm_Popup_Closed');
    });

    // этот метод вызывается после оплаты и использовать его для хука до оплаты нельзя
    paymentRequest.on('paymentmethod', async e => {
      onPayBefore?.();
      let paymentId = null;
      const attachResult = await this.attachPayment({
        store,
        paymentMethodId: e.paymentMethod.id,
      });

      if (attachResult.success) {
        paymentId = attachResult.result.id;
        logEvent('Web_Payment_New_Card_Attached');
      } else {
        logEvent('Web_Payment_Processing_Failed', {
          error: attachResult.result.code,
        });
      }

      if (paymentId === null) {
        onPay?.({ paymentName: e.walletName, isSuccess: false });

        return;
      }

      const result = await this.chargePayment({
        coupon,
        plan,
        paymentId,
        store,
      });

      store.dispatch(chargeFinish(result));

      e.complete('success');

      onPay?.({ paymentName: e.walletName, isSuccess: true });
    });

    const result: CanMakePaymentResult | null =
      await paymentRequest.canMakePayment();

    if (result === null) {
      return null;
    }

    const paymentType = Object.entries(result).find(el => el[1]);
    const paymentSystem = paymentType?.length ? paymentType[0] : null;
    const query = new QueryParser(window.location.search);
    const payment_widget = query.get('payment_widget');
    const titleUtm = query.get('title');
    const timerUtm = query.get('timer');
    const sellingPointUtm = query.get('selling_point');

    if (paymentSystem) {
      logEvent('Web_Payment_System_Button_Viewed', {
        payment_system: paymentSystem,
        payment_widget,
        title: titleUtm,
        timer: timerUtm,
        selling_point: sellingPointUtm,
      });
    }

    return [paymentRequest, paymentSystem];
  }

  async addCard({
    stripe,
    elements,
    store,
  }: {
    stripe: stripeJs.Stripe;
    elements: stripeJs.StripeElements;
    store: AppStore;
  }): Promise<boolean> {
    const state = store.getState();
    const profile: AppState['profile'] = state.profile;
    const cardElement = elements.getElement(CardNumberElement);

    if (cardElement === null) {
      return false;
    }

    const { error: paymentMethodError, paymentMethod } =
      await this.createPaymentMethod(cardElement, stripe, {
        billing_details: {
          email: profile.profile?.email,
          name: 'WELLTORY INC',
        },
      });

    if (paymentMethodError) {
      return false;
    }

    const attachResult = await this.attachPayment({
      paymentMethodId: paymentMethod.id,
      store,
    });

    return attachResult.success;
  }

  private async createPaymentMethod(
    cardElement: StripeCardNumberElement,
    stripe: stripeJs.Stripe,
    extra?: { [key: string]: object | string },
  ): Promise<PaymentMethodResult> {
    return stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      ...extra,
    });
  }

  private async attachPayment({
    paymentMethodId,
    store,
  }: {
    paymentMethodId: string;
    store: AppStore;
  }): Promise<{
    success: boolean;
    result: {
      code: string;
    } & AttachPaymentsMethodResponseType['result'];
  }> {
    return (await store
      .dispatch(attachPaymentsMethodThunk({ id: paymentMethodId }))
      .unwrap()) as never as {
      success: boolean;
      result: {
        code: string;
      } & AttachPaymentsMethodResponseType['result'];
    };
  }

  private async chargePayment({
    coupon,
    plan,
    paymentId,
    store,
  }: {
    coupon?: string;
    plan: { id: number; interval: string; discount: number; price: number };
    paymentId: string;
    store: AppStore;
  }): Promise<ChargePurchasesResponseType> {
    const result = await store
      .dispatch(
        chargePurchaseThunk({
          coupon,
          product_id: plan.id,
          payment_method_id: paymentId,
          extra: {
            interval: plan.interval,
            discount: plan.discount,
            price: plan.price,
          },
        }),
      )
      .unwrap();

    store.dispatch(chargeFinish(result));

    // @ts-ignore
    return result;
  }
}

export const stripeProcessing = new StripeProcessing();
